import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyMembership, verifyMyInfo, changeMyPassword, listClasses } from "./happyschoolApi";
import { weakPasswordReason, PASSWORD_POLICY_TEXT } from "./passwordPolicy";
import HappySchoolMyProfile from "./HappySchoolMyProfile";
import HappySchoolMyBusy from "./HappySchoolMyBusy";
import HappySchoolMyTimetable from "./HappySchoolMyTimetable";
import HappySchoolSubstituteTeachers from "./HappySchoolSubstituteTeachers";
import HappySchoolMyClasses from "./HappySchoolMyClasses";
import HappySchoolVanThu from "./HappySchoolVanThu";
import HappySchoolDirectory from "./HappySchoolDirectory";
import HappySchoolHomeroomTeachers, { ThiKhaoSatLopCn } from "./HappySchoolHomeroomTeachers";
import HappySchoolMyPayroll from "./HappySchoolMyPayroll";
import HappySchoolMyChamCong from "./HappySchoolMyChamCong";
import HappySchoolMyVanhoaNghe from "./HappySchoolMyVanhoaNghe";

// Nội quy nhà trường — nội dung mẫu tạm (sau này chủ trường có thể soạn riêng).
const NOI_QUY_MAU = [
  "Thực hiện nghiêm giờ giấc làm việc; chấm công đầy đủ mỗi buổi (quẹt máy vào - ra).",
  "Trang phục lịch sự, đúng quy định; đeo thẻ khi ở trường.",
  "Giữ gìn vệ sinh chung, bảo vệ tài sản và cơ sở vật chất nhà trường.",
  "Ứng xử văn minh với học sinh, phụ huynh và đồng nghiệp; không gây mất đoàn kết.",
  "Bảo mật thông tin học sinh, hồ sơ nội bộ; không phát tán ra ngoài.",
  "Giáo viên khai báo lịch bận đúng hạn để nhà trường xếp thời khoá biểu.",
  "Xin nghỉ phải có đơn/được phê duyệt trước; báo cáo kịp thời khi có sự cố.",
];

// Cổng dành cho Cán bộ nhân viên & Giáo viên (không phải owner/admin).
// - CBNV: Nội quy · Xác thực thông tin · Đổi mật khẩu · (sau xác thực) Hồ sơ.
// - Giáo viên: như trên + Khai báo lịch bận theo tuần TKB.
const HappySchoolStaffPortal = ({ schoolId, isTeacher, displayName }) => {
  const [view, setView] = useState("menu");
  const [verified, setVerified] = useState(false);
  const [member, setMember] = useState(null);
  const [isHomeroom, setIsHomeroom] = useState(false); // đang chủ nhiệm >= 1 lớp
  const navigate = useNavigate();

  const loadMembership = () => {
    if (!schoolId) return;
    getMyMembership(schoolId)
      .then((d) => { setMember(d.member || null); setVerified(!!(d.member && d.member.profileVerified)); })
      .catch(() => {});
  };
  useEffect(loadMembership, [schoolId]);

  // GV chủ nhiệm (classes.homeroomTeacherId === staffId của mình) được vào
  // trang "Quản lý học sinh" đầy đủ (/happyschool/student-lists) để XEM.
  useEffect(() => {
    if (!schoolId || !isTeacher || !(member && member.staffId)) { setIsHomeroom(false); return; }
    listClasses(schoolId)
      .then((cls) => setIsHomeroom((cls || []).some((c) => c.homeroomTeacherId === member.staffId && c.status !== "giai_tan")))
      .catch(() => setIsHomeroom(false));
  }, [schoolId, isTeacher, member]);

  const cards = [
    { key: "rules", icon: "📜", label: "Nội quy nhà trường", desc: "Xem nội quy, quy định làm việc của nhà trường." },
  ];
  if (isTeacher) cards.push({ key: "vanhoanghe", icon: "📚", label: "Lịch phân phối nghề-văn hoá", desc: "Xem lịch buổi nào học văn hoá, buổi nào học nghề theo từng lớp/tuần." });
  if (isTeacher) cards.push({ key: "busy", icon: "🗓️", label: "Khai báo lịch bận", desc: "Đánh dấu buổi/tiết bạn không dạy được cho từng tuần TKB." });
  if (isTeacher) cards.push({ key: "tkb", icon: "📅", label: "Xem thời khoá biểu", desc: "Xem TKB của bạn — tuần đang mở khai báo bận, bản mới nhất." });
  if (isTeacher) cards.push({ key: "baogiang", icon: "📢", label: "Lịch báo giảng", desc: "Xem thời khoá biểu Lịch báo giảng đã công bố (tách riêng khỏi TKB thường)." });
  if (isTeacher) cards.push({ key: "thikhaosat", icon: "📝", label: "Thi khảo sát — Khối 12", desc: "Xem lịch thi, phòng thi, lịch coi thi của bạn; nhập điểm môn bạn dạy.", href: "/happyschool/thi-khao-sat" });
  if (isTeacher) cards.push({ key: "thiks_cn", icon: "🪑", label: "Phòng thi lớp chủ nhiệm & chuyên môn", desc: "Thi khảo sát: danh sách học sinh các lớp bạn chủ nhiệm hoặc được phân công dạy, kèm SBD, phòng thi, điểm — in / xuất Excel." });
  if (isTeacher) cards.push({ key: "substitute", icon: "☎️", label: "Giáo viên dạy thay", desc: "Tìm GV cùng bộ môn, cùng khối để nhờ dạy thay khi bạn bận." });
  if (isTeacher) cards.push({ key: "myclasses", icon: "📁", label: "Quản lý học sinh", desc: "Lớp chủ nhiệm và lớp chuyên môn bạn đang dạy." });
  if (isTeacher && isHomeroom) cards.push({ key: "studentlists", icon: "📁", label: "Danh sách học sinh toàn trường", desc: "Dành cho GV chủ nhiệm: HSSV đang học, đã ra trường, tốt nghiệp, ưu tú, chính sách, sức khỏe... (xem).", href: "/happyschool/student-lists" });
  if (isTeacher) cards.push({ key: "directory", icon: "📇", label: "Nhân viên toàn trường", desc: "Danh bạ Họ tên + SĐT toàn bộ nhân viên, tìm nhanh theo tên hoặc số điện thoại." });
  if (isTeacher) cards.push({ key: "vanthu", icon: "🗄️", label: "Văn thư lưu trữ", desc: "Link tài liệu do bạn đưa lên (Google Sheets, Docs...) — bạn được quyền thêm." });
  cards.push({ key: "homeroom", icon: "🧑‍🎓", label: "Giáo viên chủ nhiệm", desc: "GV chủ nhiệm, SĐT, lớp chủ nhiệm, SĐT lớp trưởng/lớp phó." });
  cards.push({ key: "payroll", icon: "💵", label: "Lương của tôi", desc: verified ? "Xem các đợt thanh toán lương khớp đúng tên hồ sơ của bạn." : "🔒 Cần xác thực thông tin trước." });
  // Chấm công/Vi phạm áp dụng cho MỌI nhân sự (không riêng giáo viên) — bảng
  // "Chấm công tháng"/"Vi phạm" đã có dòng của cả nhân viên hành chính, bảo
  // vệ, bếp... nên thẻ này không giới hạn theo isTeacher.
  cards.push({ key: "chamcong", icon: "🧾", label: "Xem thu nhập", desc: verified ? "Xem chấm công tháng khớp đúng tên hồ sơ của bạn — hiện khi nhà trường đã công bố." : "🔒 Cần xác thực thông tin trước." });
  cards.push(
    { key: "verify", icon: verified ? "✅" : "🔎", label: "Xác thực thông tin", desc: verified ? "Đã xác thực — bạn có thể xem & sửa hồ sơ." : "Khớp Họ tên + CCCD + Ngày sinh để mở khoá hồ sơ." },
    { key: "profile", icon: "👤", label: "Hồ sơ của tôi", desc: verified ? "Xem và sửa thông tin cá nhân của bạn." : "🔒 Cần xác thực thông tin trước." },
    { key: "password", icon: "🔑", label: "Đổi mật khẩu", desc: "Đổi mật khẩu đăng nhập của bạn." },
  );

  const title = isTeacher ? "🧑‍🏫 Trang quản lý của Giáo viên" : "🧰 Trang của Cán bộ nhân viên";

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <style>{`
        .sp-card{display:flex;align-items:center;gap:14px;padding:16px;border:1px solid #e5e7eb;border-radius:12px;background:#fff;cursor:pointer;text-align:left;transition:all .15s;box-shadow:0 1px 3px rgba(0,0,0,.05);}
        .sp-card:hover{transform:translateY(-2px);border-color:#0ea5a3;background:#f0fdfa;}
        .sp-ic{width:52px;height:52px;border-radius:12px;display:flex;align-items:center;justify-content:center;background:#f3f4f6;font-size:24px;flex-shrink:0;}
      `}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
      </div>
      <p style={{ color: "#6b7280", fontSize: 14, marginTop: 0 }}>
        Xin chào{displayName ? <b> {displayName}</b> : (member && member.name ? <b> {member.name}</b> : "")}. Chọn chức năng bên dưới.
      </p>

      {view === "menu" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14, marginTop: 12 }}>
          {cards.map((c) => (
            <div key={c.key} className="sp-card" onClick={() => (c.href ? navigate(c.href) : setView(c.key))}>
              <div className="sp-ic">{c.icon}</div>
              <div>
                <div style={{ fontWeight: 600, color: "#1f2937" }}>{c.label}</div>
                <div style={{ fontSize: 13, color: "#4b5563" }}>{c.desc}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => { setView("menu"); loadMembership(); }} style={ghost}>← Quay lại</button>
          <div style={{ marginTop: 14 }}>
            {view === "rules" && <RulesView />}
            {view === "verify" && (
              <VerifyView
                schoolId={schoolId}
                alreadyVerified={verified}
                onVerified={() => { setVerified(true); loadMembership(); }}
                onGoProfile={() => setView("profile")}
              />
            )}
            {view === "profile" && <HappySchoolMyProfile onNeedVerify={() => setView("verify")} />}
            {view === "password" && <PasswordView />}
            {view === "vanhoanghe" && isTeacher && (
              <div style={{ padding: "0 4px" }}>
                <h2 style={{ margin: "0 0 4px" }}>📚 Lịch phân phối nghề-văn hoá</h2>
                <HappySchoolMyVanhoaNghe schoolId={schoolId} canImport={!!(member && member.canImportVanhoaNghe)} />
              </div>
            )}
            {view === "busy" && isTeacher && (
              <div style={{ padding: "0 4px" }}>
                <h2 style={{ margin: "0 0 4px" }}>🗓️ Khai báo lịch bận</h2>
                <HappySchoolMyBusy schoolId={schoolId} />
              </div>
            )}
            {view === "tkb" && isTeacher && (
              <div style={{ padding: "0 4px" }}>
                <h2 style={{ margin: "0 0 4px" }}>📅 Thời khoá biểu của tôi</h2>
                <HappySchoolMyTimetable schoolId={schoolId} onNeedVerify={() => setView("verify")} />
              </div>
            )}
            {view === "baogiang" && isTeacher && (
              <div style={{ padding: "0 4px" }}>
                <h2 style={{ margin: "0 0 4px" }}>📢 Lịch báo giảng</h2>
                <HappySchoolMyTimetable schoolId={schoolId} onNeedVerify={() => setView("verify")} baoGiangOnly />
              </div>
            )}
            {view === "substitute" && isTeacher && (
              <div style={{ padding: "0 4px" }}>
                <h2 style={{ margin: "0 0 4px" }}>☎️ Giáo viên dạy thay</h2>
                <HappySchoolSubstituteTeachers schoolId={schoolId} />
              </div>
            )}
            {view === "myclasses" && isTeacher && (
              <div style={{ padding: "0 4px" }}>
                <h2 style={{ margin: "0 0 4px" }}>📁 Quản lý học sinh</h2>
                <HappySchoolMyClasses schoolId={schoolId} staffId={member && member.staffId} />
              </div>
            )}
            {view === "directory" && isTeacher && <HappySchoolDirectory />}
            {view === "homeroom" && <HappySchoolHomeroomTeachers embedded />}
            {view === "thiks_cn" && isTeacher && <ThiKhaoSatLopCn schoolId={schoolId} />}
            {view === "vanthu" && isTeacher && (
              <HappySchoolVanThu myName={(member && member.name) || displayName} />
            )}
            {view === "payroll" && (
              verified ? <HappySchoolMyPayroll /> : (
                <div style={{ padding: 16, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, color: "#92400e" }}>
                  🔒 Cần xác thực thông tin trước mới xem được Lương của tôi (khớp đúng tên hồ sơ).
                  <div style={{ marginTop: 10 }}>
                    <button type="button" onClick={() => setView("verify")} style={verifyBtn}>🔎 Xác thực thông tin ngay</button>
                  </div>
                </div>
              )
            )}
            {view === "chamcong" && (
              verified ? <HappySchoolMyChamCong /> : (
                <div style={{ padding: 16, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, color: "#92400e" }}>
                  🔒 Cần xác thực thông tin trước mới xem được Xem thu nhập (khớp đúng tên hồ sơ).
                  <div style={{ marginTop: 10 }}>
                    <button type="button" onClick={() => setView("verify")} style={verifyBtn}>🔎 Xác thực thông tin ngay</button>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RulesView = () => (
  <div style={{ maxWidth: 720, margin: "0 auto" }}>
    <h2 style={{ margin: "0 0 4px" }}>📜 Nội quy nhà trường</h2>
    <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 0 }}>(Nội dung mẫu tạm — nhà trường sẽ cập nhật chính thức.)</p>
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 18, background: "#fff", textAlign: "left" }}>
      <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8, color: "#374151", textAlign: "left" }}>
        {NOI_QUY_MAU.map((t, i) => (<li key={i}>{t}</li>))}
      </ol>
    </div>
  </div>
);

const VerifyView = ({ schoolId, alreadyVerified, onVerified, onGoProfile }) => {
  const [name, setName] = useState("");
  const [cccd, setCccd] = useState("");
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false); // chỉ true khi xác thực ĐÚNG ở phiên này
  const [pending, setPending] = useState(false); // nhập không khớp -> đã gửi chờ duyệt

  const submit = async () => {
    if (!name.trim() || !cccd.trim() || !dob) { setError("Cần đủ Họ tên, CCCD và Ngày sinh."); return; }
    setBusy(true); setError("");
    try {
      const res = await verifyMyInfo(schoolId, { name: name.trim(), cccd: cccd.trim(), dob });
      if (res && res.status === "pending") {
        setPending(true); // không khớp hồ sơ -> đã lưu tạm, chờ owner/admin duyệt
      } else {
        setDone(true);          // nhập đúng -> chuyển sang trạng thái đã xác thực
        onVerified && onVerified();
      }
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 480, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 4px" }}>🔎 Xác thực thông tin</h2>
      {pending ? (
        <div style={waitBox}>
          ⏳ Thông tin bạn nhập <b>chưa khớp</b> hồ sơ nhà trường đang lưu.
          Đã gửi yêu cầu, chờ nhà trường (chủ trường/quản trị) kiểm tra và duyệt.
          Sau khi được duyệt, hồ sơ của bạn sẽ tự cập nhật và mở khoá xác thực.
          <div style={{ marginTop: 12 }}>
            <button type="button" onClick={() => { setPending(false); setError(""); }} style={primary}>
              🔁 Xác thực lại
            </button>
          </div>
        </div>
      ) : done ? (
        // Chỉ hiện màn hình "đã xác thực" SAU KHI nhập đúng.
        <div style={okBox}>
          ✅ Xác thực thành công! Trạng thái của bạn: <b>Đã xác thực</b>.
          <div style={{ marginTop: 12 }}>
            <button type="button" onClick={onGoProfile} style={primary}>Xem hồ sơ của tôi</button>
          </div>
        </div>
      ) : (
        <>
          <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0 }}>Nhập đúng <b>Họ tên + CCCD + Ngày sinh</b> khớp hồ sơ nhà trường để mở khoá xem & sửa hồ sơ của bạn.</p>
          {alreadyVerified && <p style={{ fontSize: 12, color: "#059669", marginTop: 0 }}>Bạn đã xác thực trước đó — có thể xác thực lại nếu cần.</p>}
          {error && <p style={{ color: "#dc2626" }}>{error}</p>}
          <div style={panel}>
            <label style={fld}><span style={lbl}>Họ và tên</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="VD: Nguyễn Văn A" style={inp} /></label>
            <label style={fld}><span style={lbl}>Căn cước công dân</span>
              <input value={cccd} onChange={(e) => setCccd(e.target.value)} placeholder="12 số" style={inp} /></label>
            <label style={fld}><span style={lbl}>Ngày sinh</span>
              <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} style={inp} /></label>
            <div style={{ marginTop: 12 }}>
              <button type="button" onClick={submit} disabled={busy} style={primary}>{busy ? "Đang xác thực..." : "Xác thực"}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const PasswordView = () => {
  const [oldPassword, setOld] = useState("");
  const [newPassword, setNew] = useState("");
  const [newPassword2, setNew2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const submit = async () => {
    setError(""); setOk("");
    if (!oldPassword || !newPassword) { setError("Nhập mật khẩu cũ và mới."); return; }
    const weak = weakPasswordReason(newPassword, "");
    if (weak) { setError(`${weak} ${PASSWORD_POLICY_TEXT}`); return; }
    if (newPassword === oldPassword) { setError("Mật khẩu mới phải khác mật khẩu cũ."); return; }
    if (newPassword !== newPassword2) { setError("Mật khẩu mới nhập lại không khớp."); return; }
    setBusy(true);
    try {
      await changeMyPassword(oldPassword, newPassword);
      setOk("Đổi mật khẩu thành công."); setOld(""); setNew(""); setNew2("");
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 480, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 4px" }}>🔑 Đổi mật khẩu</h2>
      <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0 }}>Lần đầu đăng nhập mật khẩu trùng số điện thoại — hãy đổi sang mật khẩu riêng. {PASSWORD_POLICY_TEXT}</p>
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {ok && <p style={{ color: "#059669" }}>{ok}</p>}
      <div style={panel}>
        <label style={fld}><span style={lbl}>Mật khẩu cũ</span>
          <input type="password" value={oldPassword} onChange={(e) => setOld(e.target.value)} style={inp} /></label>
        <label style={fld}><span style={lbl}>Mật khẩu mới (≥ 8 ký tự, có chữ hoa, chữ thường, số)</span>
          <input type="password" value={newPassword} onChange={(e) => setNew(e.target.value)} style={inp} /></label>
        <label style={fld}><span style={lbl}>Nhập lại mật khẩu mới</span>
          <input type="password" value={newPassword2} onChange={(e) => setNew2(e.target.value)} style={inp} /></label>
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={submit} disabled={busy} style={primary}>{busy ? "Đang đổi..." : "Đổi mật khẩu"}</button>
        </div>
      </div>
    </div>
  );
};

const panel = { border: "1px solid #e5e7eb", borderRadius: 12, padding: 18, background: "#fff" };
const fld = { display: "block", marginBottom: 10 };
const lbl = { display: "block", fontSize: 13, fontWeight: 600, color: "#4b5563", marginBottom: 4 };
const inp = { width: "100%", padding: 9, border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, boxSizing: "border-box" };
const primary = { padding: "9px 18px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 };
const ghost = { padding: "8px 14px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer" };
const okBox = { padding: 16, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, color: "#065f46" };
const waitBox = { padding: 16, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, color: "#92400e", lineHeight: 1.6 };
const verifyBtn = { padding: "8px 16px", background: "#b45309", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 };

export default HappySchoolStaffPortal;
